import type { DialogProps } from '@flex-design-system/fx';
import { Button, Dialog, Typography } from '@flex-design-system/fx';
import { styled } from '@flex-design-system/stitches-react';
import { Translation, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';

export type ExcludedSensitiveInformationItem = 'handicap' | 'veteran';

interface Props extends DialogProps {
  isSubmitting: boolean;
  onSubmit: VoidFunction;
  // NOTE: 민감정보 미동의 상태에서 민감정보(장애·보훈)를 입력한 채 지원할 때, 제출에서 제외되는 항목을 안내한다 (EPDIV-1271, EPDIV-1572).
  excludedSensitiveInformationItems?: ExcludedSensitiveInformationItem[];
}

export function ConfirmApplyDialog({
  onSubmit,
  isSubmitting,
  excludedSensitiveInformationItems = [],
  ...props
}: Props) {
  const { t } = useTranslation();

  const excludedItemsLabel = excludedSensitiveInformationItems
    .map(item => {
      return t(
        item === 'handicap'
          ? trans(
              'recruiting.site_builder.job_description.apply.confirm.sensitive_excluded.item.handicap'
            )
          : trans(
              'recruiting.site_builder.job_description.apply.confirm.sensitive_excluded.item.veteran'
            )
      );
    })
    .join(', ');

  return (
    <Dialog {...props}>
      <Dialog.Content>
        <Dialog.Body>
          <Dialog.UnbalancedTitle>
            <Translation
              tKey={trans(
                'recruiting.site_builder.job_description.apply.confirm.title'
              )}
            />
          </Dialog.UnbalancedTitle>
          <Dialog.UnbalancedDescription>
            <Translation
              tKey={trans(
                'recruiting.site_builder.job_description.apply.confirm.description'
              )}
            />
          </Dialog.UnbalancedDescription>
          {excludedSensitiveInformationItems.length > 0 ? (
            <ExcludedItemsNotice>
              <Typography size={13} color="gray" style={{ lineHeight: 1.4 }}>
                <Translation
                  tKey={trans(
                    'recruiting.site_builder.job_description.apply.confirm.sensitive_excluded.description'
                  )}
                  tOptions={{ values: { items: excludedItemsLabel } }}
                />
              </Typography>
            </ExcludedItemsNotice>
          ) : null}
        </Dialog.Body>
        <Dialog.Footer orientation="vertical">
          <Button
            type="submit"
            size="large"
            color="grayDarker"
            loading={isSubmitting}
            onClick={onSubmit}
          >
            <Translation
              tKey={trans(
                'recruiting.site_builder.job_description.apply.short'
              )}
            />
          </Button>
          <Dialog.Cancel asChild>
            <Button size="large" color="grayDarker" variant="outline">
              <Translation tKey={trans('recruiting.cancel.short')} />
            </Button>
          </Dialog.Cancel>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

const ExcludedItemsNotice = styled('div', {
  marginTop: 10,
  width: '100%',
  borderRadius: 10,
  backgroundColor: '$grayLightest',
  boxShadow: 'inset 0 0 0 1px rgba(36, 42, 48, 0.02)',
  padding: '12px 14px',
});
